import type { PayrollStatementNotificationRecipient } from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import { getNotificationReasonLabel } from "./labels";

export const getTextableRecipientsCount = (
  recipients: PayrollStatementNotificationRecipient[],
) => recipients.filter(isTextable).length;

const isTextable = (recipient: PayrollStatementNotificationRecipient) =>
  !!recipient.phone_number && !recipient.sms_ineligible_reason;

export const getTextOnlyReach = (
  recipients: PayrollStatementNotificationRecipient[],
) => ({
  setupCount: recipients.filter((recipient) => recipient.needs_account_setup)
    .length,
  unreachedCount: recipients.filter(
    (recipient) => !isTextable(recipient) && !recipient.needs_account_setup,
  ).length,
});

export const isRenotifyOnly = (
  recipients: Pick<PayrollStatementNotificationRecipient, "sent_pack">[],
) => recipients.length > 0 && recipients.every((r) => !!r.sent_pack);

export const getSelectedEligibleRecipients = (
  recipients: PayrollStatementNotificationRecipient[],
  selectedRecipientIds: Set<string>,
) =>
  recipients.filter(
    (recipient) =>
      selectedRecipientIds.has(recipient.technician_id) && recipient.eligible,
  );

export const getDefaultSelectedRecipientIds = (
  recipients: PayrollStatementNotificationRecipient[],
) =>
  new Set(
    recipients
      .filter((recipient) => recipient.eligible)
      .map((recipient) => recipient.technician_id),
  );

const areSetsEqual = <T>(first: Set<T>, second: Set<T>) =>
  first.size === second.size &&
  Array.from(first).every((value) => second.has(value));

export const reconcileSelectedRecipientIds = ({
  recipients,
  selectedRecipientIds,
  useDefaultSelection,
}: {
  recipients: PayrollStatementNotificationRecipient[];
  selectedRecipientIds: Set<string>;
  useDefaultSelection: boolean;
}) => {
  const eligibleRecipientIds = getDefaultSelectedRecipientIds(recipients);
  if (useDefaultSelection) return eligibleRecipientIds;

  const nextSelectedRecipientIds = new Set(
    Array.from(selectedRecipientIds).filter((id) =>
      eligibleRecipientIds.has(id),
    ),
  );

  return areSetsEqual(selectedRecipientIds, nextSelectedRecipientIds)
    ? selectedRecipientIds
    : nextSelectedRecipientIds;
};

export const getSendDisabledReason = ({
  packsReady = true,
  canSend,
  cannotSendReason,
  selectedEligibleCount,
}: {
  packsReady?: boolean;
  canSend: boolean;
  cannotSendReason?: string | null;
  selectedEligibleCount: number;
}) => {
  if (!packsReady)
    return "PayPacks are still being prepared. Publish once they're ready.";
  if (!canSend) {
    return (
      getNotificationReasonLabel(cannotSendReason) ??
      "This period can't be published yet."
    );
  }
  if (selectedEligibleCount === 0)
    return "Select at least one eligible technician";
  return null;
};

export const sortPayrollStatementRecipients = (
  recipients: PayrollStatementNotificationRecipient[],
  awaitingReplyTechnicianIds?: Set<string>,
): PayrollStatementNotificationRecipient[] =>
  [...recipients].sort((first, second) => {
    const availabilityOrder =
      Number(second.statement_preview_available === true) -
      Number(first.statement_preview_available === true);
    if (availabilityOrder !== 0) return availabilityOrder;

    const awaitingReplyOrder =
      Number(awaitingReplyTechnicianIds?.has(second.technician_id) === true) -
      Number(awaitingReplyTechnicianIds?.has(first.technician_id) === true);
    if (awaitingReplyOrder !== 0) return awaitingReplyOrder;

    return first.name.localeCompare(second.name, undefined, {
      sensitivity: "base",
    });
  });
